import { useCallback, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, CheckCircle2, Disc, Loader2, Sparkles, UserPlus } from "lucide-react";
import { useTranslation } from "react-i18next";
import InvestorLogo from "../../../components/data/InvestorLogo";
import CountryFlag from "../../../components/countryFlags/countryFlag";
import { Button } from "../../../components/ui/button";
import { buildInvestorProfilePath } from "../../../components/investor-display/helpers";
import { useCRM } from "../../../hooks/crm";
import { useMembershipAccess } from "../../../hooks/membership";

function formatUsd(amount) {
  if (amount == null || Number.isNaN(Number(amount))) return "";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(Number(amount));
}

function formatDate(date) {
  if (!date) return "";
  return new Date(date).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export default function InvestorsPreview({ investors, notes }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { canAddInvestor } = useMembershipAccess();
  const {
    boards,
    selectedBoard,
    selectBoard,
    loading: boardsLoading,
    addInvestorToBoard,
    isInvestorInBoard,
  } = useCRM();
  const [loadingInvestorId, setLoadingInvestorId] = useState(null);
  const [actionError, setActionError] = useState(null);
  const items = Array.isArray(investors) ? investors : [];
  const notesByInvestor = useMemo(() => {
    const map = new Map();
    for (const note of Array.isArray(notes) ? notes : []) {
      if (note?.investorId) map.set(String(note.investorId), note);
    }
    return map;
  }, [notes]);

  const generateLink = (investor) => {
    const path = buildInvestorProfilePath({
      investor,
      language: i18n.language,
      inApp: true,
    });
    return `${path}${location.search || ""}`;
  };

  const canUseSelectedBoard = Boolean(canAddInvestor && selectedBoard && !boardsLoading);

  const handleAddInvestor = useCallback(async (investor) => {
    if (!canUseSelectedBoard || !investor?._id) return;

    setLoadingInvestorId(investor._id);
    setActionError(null);
    try {
      await addInvestorToBoard({
        boardId: selectedBoard.board_id,
        columnId: 1,
        investor: {
          ...investor,
          short_info: investor.shortInfo || investor.short_info,
        },
      });
    } catch (error) {
      console.error("Error adding investor from AI funding:", error);
      setActionError(t("app:AiFunding.crmAddFailed"));
    } finally {
      setLoadingInvestorId(null);
    }
  }, [addInvestorToBoard, canUseSelectedBoard, selectedBoard, t]);

  const boardSelector = (
    <div className="flex flex-col gap-1 sm:items-end">
      <label className="text-xs font-medium text-stone-500" htmlFor="ai-funding-board-select">
        {t("app:AiFunding.crmBoardLabel")}
      </label>
      <select
        id="ai-funding-board-select"
        value={selectedBoard?.board_id || ""}
        onChange={(event) => selectBoard(event.target.value)}
        disabled={!canAddInvestor || boardsLoading || boards.length === 0}
        className="h-8 min-w-44 rounded-lg border border-stone-200 bg-white px-3 text-xs font-medium text-stone-700 shadow-sm outline-none focus:border-stone-300 focus:ring-4 focus:ring-stone-100 disabled:cursor-not-allowed disabled:bg-stone-100 disabled:text-stone-400"
      >
        {boardsLoading ? (
          <option value="">{t("app:AiFunding.crmBoardsLoading")}</option>
        ) : boards.length === 0 ? (
          <option value="">{t("app:AiFunding.crmBoardsEmpty")}</option>
        ) : (
          boards.map((board) => (
            <option key={board.board_id} value={board.board_id}>
              {board.board_name}
            </option>
          ))
        )}
      </select>
    </div>
  );

  return (
    <section className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-xs font-medium text-stone-600">
            <Sparkles className="h-3.5 w-3.5" />
            {t("app:AiFunding.investorsBadge")}
          </div>
          <h2 className="mt-3 text-xl font-semibold tracking-tight text-stone-950">
            {t("app:AiFunding.topInvestorsTitle")}
          </h2>
          <p className="mt-2 text-sm leading-6 text-stone-600">
            {t("app:AiFunding.investorsDescription")}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-end gap-3">
          {boardSelector}
          <div className="inline-flex h-8 items-center justify-center gap-2 rounded-full border border-stone-200 bg-white px-3 text-xs font-medium text-stone-700 shadow-sm">
            <Disc className="h-3.5 w-3.5" />
            {t("app:AiFunding.investorsCount", { count: items.length })}
          </div>
        </div>
      </div>

      {actionError ? (
        <div className="mt-4 rounded-lg border border-red-100 bg-red-50/60 px-3 py-2 text-xs text-red-700">
          {actionError}
        </div>
      ) : null}

      {!canAddInvestor ? (
        <div className="mt-4 rounded-lg border border-amber-100 bg-amber-50/70 px-3 py-2 text-xs leading-5 text-amber-800">
          {t("app:AiFunding.crmRequiresPaidPlan")}
        </div>
      ) : boards.length === 0 && !boardsLoading ? (
        <div className="mt-4 rounded-lg border border-stone-200 bg-stone-50/70 px-3 py-2 text-xs leading-5 text-stone-600">
          {t("app:AiFunding.crmNoBoardsHint")}
        </div>
      ) : null}

      {items.length === 0 ? (
        <div className="mt-5 rounded-lg border border-dashed border-stone-300 bg-stone-50/70 px-4 py-3 text-sm leading-6 text-stone-600">
          {t("app:AiFunding.noInvestors")}
        </div>
      ) : (
        <ul className="mt-5 space-y-3">
          {items.map((investor) => {
            const note = notesByInvestor.get(String(investor._id));
            const latestDeal = investor.latestDeal;
            const typeText = Array.isArray(investor.type) ? investor.type.slice(0, 2).join(" / ") : "";
            const investorIsLoading = loadingInvestorId === investor._id;
            const investorInSelectedBoard = selectedBoard
              ? isInvestorInBoard(investor._id, selectedBoard.board_id)
              : false;
            return (
              <li key={investor._id} className="rounded-lg border border-stone-200 bg-stone-50/70 p-4">
                <div className="flex items-start gap-3">
                  <InvestorLogo
                    id={investor.id}
                    country={investor.country}
                    className="h-9 w-9 flex-none"
                    alt={`${investor.name} logo`}
                  />

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <Link
                          to={generateLink(investor)}
                          className="group inline-flex max-w-full items-center gap-1 text-sm font-semibold leading-5 text-stone-950 hover:underline"
                        >
                          <span className="truncate">{investor.name}</span>
                          <ArrowRight className="h-3.5 w-3.5 shrink-0 transition-transform group-hover:translate-x-0.5" />
                        </Link>
                        {typeText ? <div className="mt-1 text-xs text-stone-500">{typeText}</div> : null}
                      </div>

                      {investor.country ? (
                        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-white px-2.5 py-1 text-xs font-medium text-stone-600 ring-1 ring-stone-200">
                          <CountryFlag countryName={investor.country} className="h-3 w-5" />
                          <span>{investor.country}</span>
                        </span>
                      ) : null}
                    </div>

                    <div className="mt-3">
                      {investorIsLoading ? (
                        <Button
                          variant="default"
                          size="xs"
                          wrap="nowrap"
                          disabled
                          iconLeft={<Loader2 className="animate-spin" />}
                          className="min-w-[112px] !bg-stone-100 !text-stone-950 !cursor-default"
                          aria-busy="true"
                        >
                          {t("app:AiFunding.crmAdding")}
                        </Button>
                      ) : investorInSelectedBoard ? (
                        <Button
                          variant="default"
                          size="xs"
                          wrap="nowrap"
                          disabled
                          iconLeft={<CheckCircle2 />}
                          className="min-w-[112px] !bg-stone-100 !text-stone-950 !cursor-default"
                          title={t("app:AiFunding.crmAlreadyAdded")}
                        >
                          {t("app:AiFunding.crmAdded")}
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          onClick={() => handleAddInvestor(investor)}
                          variant="default"
                          size="xs"
                          wrap="nowrap"
                          iconLeft={<UserPlus />}
                          className="min-w-[112px] !bg-stone-100 !text-stone-950"
                          disabled={!canUseSelectedBoard}
                          aria-disabled={!canUseSelectedBoard}
                          title={canUseSelectedBoard ? t("app:AiFunding.crmAddToBoard", { boardName: selectedBoard?.board_name }) : t("app:AiFunding.crmChooseBoard")}
                        >
                          {t("app:AiFunding.crmAdd")}
                        </Button>
                      )}
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                      <span className="inline-flex rounded-full bg-white px-3 py-1 text-xs font-medium text-stone-600 ring-1 ring-stone-200">
                        {t("app:AiFunding.matchingDeals", { count: investor.freq || 0 })}
                      </span>
                      {investor.leadFreq ? (
                        <span className="inline-flex rounded-full bg-white px-3 py-1 text-xs font-medium text-stone-600 ring-1 ring-stone-200">
                          {t("app:AiFunding.leadDeals", { count: investor.leadFreq })}
                        </span>
                      ) : null}
                      {latestDeal?.amount ? (
                        <span className="inline-flex rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                          {formatUsd(latestDeal.amount)}
                        </span>
                      ) : null}
                    </div>

                    {latestDeal ? (
                      <div className="mt-3 text-xs leading-5 text-stone-500">
                        {t("app:AiFunding.latestInvestorDeal", {
                          round: latestDeal.round || "—",
                          date: formatDate(latestDeal.date) || "—",
                        })}
                      </div>
                    ) : null}

                    {note?.reason ? (
                      <div className="mt-3 rounded-lg border border-stone-200 bg-white px-3 py-2 text-xs leading-5 text-stone-600">
                        <span className="font-medium text-stone-800">{t("app:AiFunding.whyThisInvestor")}</span>{" "}
                        {note.reason}
                        {note.outreachAngle ? <div className="mt-1">{note.outreachAngle}</div> : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
